import { useImagePicker } from "@repo/components/src/hooks/image";
import { createFile } from "@repo/components/src/hooks/image/useImagePicker";
import { Box } from "@repo/components/src/ui/box";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Icon } from "@repo/components/src/ui/icon";
import { Image } from "@repo/components/src/ui/image";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import fileSchema from "@repo/components/src/utils/yup/fileSchema";
import ReactNativeFile from "@repo/graphql/client/file";
import { MediaType } from "expo-image-picker";
import { AlertCircleIcon, FileImage, TrashIcon } from "lucide-react-native";
import { ComponentProps, useCallback } from "react";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldArrayPath,
  FieldError,
  FieldValues,
  RefCallBack,
  useController,
  useFieldArray,
} from "react-hook-form";
import * as yup from "yup";
import { useDraggableFiles } from "../../hooks/drag";
import { Button, ButtonIcon } from "../../ui/button";
import { Card } from "../../ui/card";
import { Heading } from "../../ui/heading";
import { HStack } from "../../ui/hstack";
import { VStack } from "../../ui/vstack";
import formatBytesToHuman from "../../utils/bytes";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";
import ImageTile from "./ImageTile";

type FileSchema = yup.InferType<typeof fileSchema>;

type FileFieldEntry = ReactNativeFile & { _key: string };

type FileFieldArray = {
  fields: FileFieldEntry[];
  append: (files: ReactNativeFile[]) => void;
  remove: (index: number) => void;
  move: (from: number, to: number) => void;
};

type FileFieldController = {
  field: { ref: RefCallBack };
  fieldState: { error: FieldError | undefined };
};

type FormInputDragFileProps<TFieldValues extends FieldValues> = Omit<
  FormInputProps<TFieldValues, FileSchema | FileSchema[] | null>,
  "size" | "placeholder" | "required"
> & {
  iconSize?: ComponentProps<typeof Icon>["size"];
  size?: ComponentProps<typeof FormControl>["size"];
  iconClassName?: string;
  accept?: MediaType | MediaType[];
  helperText?: string;
  multiple?: boolean;
  orderable?: boolean;
};

export default function FormInputDragFile<TFieldValues extends FieldValues>(
  props: FormInputDragFileProps<TFieldValues>,
) {
  const { multiple = true } = props;
  if (!multiple) {
    return <SingleDragFile {...props} />;
  }
  return <MultipleDragFile {...props} />;
}

function SingleDragFile<TFieldValues extends FieldValues>(
  props: FormInputDragFileProps<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    labelSize,
    disabled,
    className,
    iconSize = "md",
    iconClassName = "",
    accept = "images",
    size = "md",
    helperText,
  } = props;

  const { isDragging, dropToRnFiles, draggableProps } = useDraggableFiles();

  const { onPress } = useImagePicker({
    options: {
      mediaTypes: accept,
      allowsMultipleSelection: false,
      selectionLimit: undefined,
    },
  });

  const onDrop = useCallback(
    async (
      e: React.DragEvent<HTMLDivElement>,
      onChange: ControllerRenderProps<TFieldValues>["onChange"],
    ) => {
      const newFiles = await dropToRnFiles(e);
      const validFiles = newFiles.filter((file) =>
        accept === "images" ? file.type.startsWith("image/") : true,
      );
      validFiles.length > 0 && onChange(validFiles[0]);
    },
    [dropToRnFiles, accept],
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
        return (
          <FormControl
            isInvalid={!!error}
            isDisabled={disabled}
            className={className}
            size={size}
            ref={focusScrollRef(ref)}
          >
            {!!label && (
              <FormControlLabel>
                <FormControlLabelText size={labelSize}>
                  {label}
                </FormControlLabelText>
              </FormControlLabel>
            )}
            <Pressable
              onPress={async () => {
                const newFile = await onPress();
                newFile && onChange(createFile(newFile[0]));
              }}
            >
              {(pressableProps: any) => (
                <div
                  {...draggableProps}
                  onDrop={(e: React.DragEvent<HTMLDivElement>) =>
                    onDrop(e, onChange)
                  }
                >
                  <Box
                    className={`border-2 border-dashed border-brand-300 rounded-lg py-8 bg-brand-50 ${
                      pressableProps.pressed || isDragging ? "opacity-50" : ""
                    }`}
                  >
                    <Box className="flex items-center justify-center space-y-2">
                      <Icon
                        as={FileImage}
                        size={iconSize}
                        className={`text-typography-400 ${iconClassName}`}
                      />
                      <Text className="text-typography-500 text-center">
                        {helperText || "Arrastra y suelta un archivo aquí"}
                      </Text>
                    </Box>
                  </Box>
                </div>
              )}
            </Pressable>
            <VStack space="sm" className="mt-2">
              {!!value && (
                <Card variant="filled" size="md">
                  <HStack space="md" className="items-start">
                    <Image
                      className="rounded-xl"
                      source={{ uri: value.uri }}
                      size="xs"
                      alt="Foto de la campaña"
                    />
                    <VStack space="xs" className="flex-1 shrink">
                      <Heading size="2xs">{value.name}</Heading>
                      <Text action="muted" size="xs">
                        {formatBytesToHuman(value.fileSize ?? 0)}
                      </Text>
                    </VStack>
                    <Button
                      size="none"
                      variant="link"
                      onPress={() => {
                        onChange(null);
                      }}
                    >
                      <ButtonIcon as={TrashIcon} size="xs" />
                    </Button>
                  </HStack>
                </Card>
              )}
            </VStack>
            <FormControlError>
              <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
              <FormControlErrorText>{error?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}

function MultipleDragFile<TFieldValues extends FieldValues>(
  props: FormInputDragFileProps<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    labelSize,
    disabled,
    className,
    iconSize = "md",
    iconClassName = "",
    accept = "images",
    size = "md",
    helperText,
    orderable = false,
  } = props;

  const { isDragging, dropToRnFiles, draggableProps } = useDraggableFiles();

  const { onPress } = useImagePicker({
    options: {
      mediaTypes: accept,
      allowsMultipleSelection: true,
      selectionLimit: 10,
    },
  });

  // react-hook-form's Path/ArrayPath machinery can't be proven generically
  // from within a reusable wrapper component: it filters the concrete
  // form's key set by value type, but a component generic over TFieldValues
  // never gets a concrete key set to filter, so `FieldArray<TFieldValues, TName>`
  // stays unresolved. Bridging through the library's own untyped `FieldValues`
  // escape hatch, then asserting the (documented, house-convention) real shape
  // of the array items, is the accepted way out for a generic wrapper like this.
  const arrayControl = control as unknown as Control<FieldValues>;
  const arrayName = name as unknown as FieldArrayPath<FieldValues>;

  const { fields, append, remove, move } = useFieldArray({
    control: arrayControl,
    name: arrayName,
    keyName: "_key",
  }) as unknown as FileFieldArray;

  const {
    field: { ref },
    fieldState: { error },
  } = useController({
    control: arrayControl,
    name: arrayName,
  }) as unknown as FileFieldController;

  const onDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      const newFiles = await dropToRnFiles(e);
      const validFiles = newFiles.filter((file) =>
        accept === "images" ? file.type.startsWith("image/") : true,
      );
      validFiles.length > 0 && append(validFiles);
    },
    [dropToRnFiles, accept, append],
  );

  return (
    <FormControl
      isInvalid={!!error}
      isDisabled={disabled}
      className={className}
      size={size}
      ref={focusScrollRef(ref)}
    >
      {!!label && (
        <FormControlLabel>
          <FormControlLabelText size={labelSize}>{label}</FormControlLabelText>
        </FormControlLabel>
      )}
      <Pressable
        onPress={async () => {
          const newFile = await onPress();
          newFile && append(newFile.map(createFile));
        }}
      >
        {(pressableProps: any) => (
          <div {...draggableProps} onDrop={onDrop}>
            <Box
              className={`border-2 border-dashed border-brand-300 rounded-lg py-8 bg-brand-50 ${
                pressableProps.pressed || isDragging ? "opacity-50" : ""
              }`}
            >
              <Box className="flex items-center justify-center space-y-2">
                <Icon
                  as={FileImage}
                  size={iconSize}
                  className={`text-typography-400 ${iconClassName}`}
                />
                <Text className="text-typography-500 text-center">
                  {helperText || "Arrastra y suelta un archivo aquí"}
                </Text>
              </Box>
            </Box>
          </div>
        )}
      </Pressable>
      <VStack space="sm" className="mt-2">
        {orderable ? (
          <HStack space="sm" className="flex-wrap">
            {fields.map((field, index) => (
              <ImageTile
                key={field._id}
                file={field}
                index={index}
                total={fields.length}
                onRemove={() => remove(index)}
                onMoveLeft={() => move(index, index - 1)}
                onMoveRight={() => move(index, index + 1)}
              />
            ))}
          </HStack>
        ) : (
          fields.map((field, index) => (
            <Card variant="filled" size="md" key={field._id}>
              <HStack space="md" className="items-start">
                <Image
                  className="rounded-xl"
                  source={{ uri: field.uri }}
                  size="xs"
                  alt="Foto de la campaña"
                />
                <VStack space="xs" className="flex-1 shrink">
                  <Heading size="2xs">{field.name}</Heading>
                  <Text action="muted" size="xs">
                    {formatBytesToHuman(field.fileSize ?? 0)}
                  </Text>
                </VStack>
                <Button
                  size="none"
                  variant="link"
                  onPress={() => remove(index)}
                >
                  <ButtonIcon as={TrashIcon} size="xs" />
                </Button>
              </HStack>
            </Card>
          ))
        )}
      </VStack>
      <FormControlError>
        <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
        <FormControlErrorText>{error?.message}</FormControlErrorText>
      </FormControlError>
    </FormControl>
  );
}
